import { FormControl, FormLabel, Radio, Select, Stack } from '@chakra-ui/react'
import { CheckboxFormControl } from 'BrokerTsLib/src/components'
import type { PersonAdditionalData } from 'BrokerTsLib/src/dtos'
import { Product, Selection, SelectionType } from 'BrokerTsLib/src/enums'
import { useFormContext } from 'BrokerTsLib/src/rhf'

import AgentPerson from '@/containers/forms/person/AgentPerson'

import RuianAddressSelector from '@/components/RuianAddressSelector'
import { useApp } from '@/hooks/useApp'
import type { Step3FormValues } from '../..'
import { useInsuranceParams } from '../../../../../../context/InsuranceParamsProvider'
import { useSelections } from '../../../../../../hooks'
import { useNameof } from '../../../../../../hooks/useNameof'
import { FormBody } from '../../../../../forms/FormBody'
import { FormRow } from '../../../../../forms/FormRow'
import ICOOptions from '../../../../../forms/person/ICOOptions'
import InsureeContact from '../../../../../forms/person/InsureeContact'
import InsureeName from '../../../../../forms/person/InsureeName'
import { PersonID } from '../../../../../forms/person/PersonID'
import PersonType from '../../../../../forms/person/PersonType'
import { RadioGroup } from '../../../../../forms/RadioGroup'
import type { AdditionalInfoFormProps } from './AdditionalHolderInfoForm'



export const AdditionalOwnerInfoForm = ({ isDisabledChanges }: AdditionalInfoFormProps) => {
  const { product } = useApp()
  const { isDisabled } = useInsuranceParams()

  const { watch, register, getValues, setValue, control } = useFormContext<Step3FormValues>()

  const nameof = useNameof<Step3FormValues>()
  const personPrefix = nameof(x => x.policyHolder.owner)
  const pnameof = useNameof<PersonAdditionalData>(personPrefix)
  const showAgent = watch('policyHolder.owner.agent.required')

  const { data: dataTitles } = useSelections(SelectionType.TitleBefore)

  const ownerSameAs = watch('policyHolder.ownerSameAs')
  const holderSameAs = watch('policyHolder.holderSameAs')
  const pType = watch('policyHolder.owner.type')

  const sameAsOptions = [
    { label: 'Stejný s pojistníkem', value: Selection.PersonSameAsPolicyHolder },
    ...(product == Product.VehicleLeasingInsurance ? [{ label: 'Stejný s klientem', value: Selection.PersonSameAsClient }] : []),
    ...(!holderSameAs || ownerSameAs == Selection.PersonSameAsKeeper ? [{ label: 'Stejný s držitelem/provozovatelem', value: Selection.PersonSameAsKeeper }] : []),
    { label: 'Jiný', value: null },
  ]

  return (
    <>
      <FormBody>
        <FormControl>
          <FormLabel>Vlastník vozidla</FormLabel>

          <RadioGroup
            {...register('policyHolder.ownerSameAs')}
            value={ownerSameAs?.toString()}
            isDisabled={isDisabledChanges || isDisabled}
            onChange={(value) => setValue('policyHolder.ownerSameAs', parseInt(value))}
          >
            <Stack direction="row" spacing={16}>
              {sameAsOptions?.map((item) => (
                <Radio key={item.value} value={item.value?.toString() ?? ''}>
                  {item.label}
                </Radio>
              ))}
            </Stack>
          </RadioGroup>
        </FormControl>

        {!ownerSameAs &&
          <>
            <PersonType personPrefix={personPrefix} disabled={true} />

            {([Selection.Company, Selection.SelfEmployed] as Selection[]).includes(pType) &&
              <CheckboxFormControl name={pnameof(x => x.isVatPayer)} isDisabled={true} label="Plátce DPH" />
            }

            {([Selection.Person, Selection.SelfEmployed] as Selection[]).includes(pType) &&
              <PersonID personPrefix={personPrefix} disabled={true} />
            }

            {([Selection.Company, Selection.SelfEmployed] as Selection[]).includes(pType) &&
              <ICOOptions personPrefix={personPrefix} disabled={true} />
            }

            {([Selection.Person, Selection.SelfEmployed] as Selection[]).includes(pType) &&
              <FormRow itemsCount={3}>
                <FormControl>
                  <FormLabel>Titul před jménem</FormLabel>

                  {(dataTitles?.data.length ?? 0) > 0 &&
                    <Select
                      {...register('policyHolder.owner.titleBefore')}
                      isDisabled={isDisabled}
                      defaultValue={getValues('policyHolder.owner.titleBefore') ?? ''}
                    >
                      <option value=''>Vyberte</option>
                      {dataTitles?.data.map((item) => (
                        <option value={item.value?.toString()} key={item.id}>{item.value}</option>
                      ))}
                    </Select>
                  }
                </FormControl>

                <InsureeName personPrefix={personPrefix} disabled={true} />
              </FormRow>
            }

            <RuianAddressSelector
              addressPath={pnameof((x) => x.address)}
              isDisabled={true}
              showOnlyLotNumber={false}
            />

            {showAgent &&
              <AgentPerson personPrefix={personPrefix} />
            }

            <InsureeContact personPrefix={personPrefix} isDisabled={isDisabled} />
          </>
        }

      </FormBody>
    </>
  )
}